import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useAuth } from '../../context/AuthContext.js';
import type { RoleOption } from '../../hooks/useRoleOptions.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { BanDialog } from './BanDialog.js';
import { BanReasonBox } from './BanReasonBox.js';
import { ChangeCampusModal } from './ChangeCampusModal.js';
import { IdentityNumberRow } from './IdentityNumberRow.js';
import { IdentityReview } from './IdentityReview.js';
import { ImpersonateButton } from './ImpersonateButton.js';
import { SupplierMemberships } from './SupplierMemberships.js';
import { UserEditForm } from './UserEditForm.js';
import { UserEventTimeline } from './UserEventTimeline.js';
import type { BanInfo, UnpaidPenalty } from './banReason.js';

interface User {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  status: 'active' | 'inactive' | 'banned';
  /** 204 (#283, scoped): set only by account closure — never by any other existing path. */
  deleted_at?: string | null;
  email_verified_at?: string;
  created_at: string;
  identity_verification?: { status: string };
  roles?: string[];
  /** 034: live supplier memberships (each with its organisation role); 376 counts their roles on the edit form. */
  memberships?: Array<{ role: string }>;
  /** 331: whether this user is an administrator (their roles grant manage User); sent to staff who manage users. */
  is_administrator?: boolean;
  /** 381 (#1271): why the account is banned (staff only); null when it is not. */
  ban?: BanInfo | null;
  /** 381: the customer's unpaid penalties, offered by the ban dialog. */
  unpaid_penalties?: UnpaidPenalty[];
  /** 332: whether THIS viewer may manage the account (only a SuperAdmin manages a SuperAdmin). Absent means yes. */
  manageable?: boolean;
  /** 333: why the viewer may not manage the account: a SuperAdmin target, or one whose powers the viewer's do not
   * cover. Absent (an older backend) reads as 332's reason. */
  manage_refusal?: 'superadmin_only' | 'not_covered' | null;
  /** 333: the roles the edit form offers that this viewer may grant. Absent means the form's own list. */
  assignable_roles?: string[];
  /** 367: every role, with whether this viewer may give it and why not. */
  role_options?: RoleOption[];
  /** 330: the customer's campus, with its name (including a deactivated one). */
  campus?: { _id: string; name: string; is_active: boolean } | null;
  /** 366: the masked IC number (never the number) and whether the customer must still give one. */
  identity_number?: { kind: 'mykad' | 'passport'; country: string; masked: string } | null;
  identity_number_required?: boolean;
}

interface Booking {
  /** 053: canonical identifier. `_id` is still on the wire but deprecated. */
  id: string;
  status: string;
  pickup_at: string;
  return_at: string;
  total_price_minor: number;
}

interface BookingsResponse {
  items: Booking[];
  total: number;
}

const bookingColumns: Column<Booking>[] = [
  {
    key: 'id',
    header: 'ID',
    render: (row) => (
      <Link to={`/bookings/${row.id}`} className="font-mono text-xs text-primary hover:underline">
        {row.id.slice(-8)}
      </Link>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => <StatusBadge status={row.status} />,
  },
  {
    key: 'pickup_at',
    header: 'Pickup',
    render: (row) =>
      new Date(row.pickup_at).toLocaleDateString('en-MY', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
  },
  {
    key: 'total_price_minor',
    header: 'Total',
    render: (row) => `RM ${(row.total_price_minor / 100).toFixed(2)}`,
  },
];

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  // 330 (US3): staff who manage users may change a customer's campus, with a reason.
  const canManageUsers = useAbility().can('manage', 'User');
  const [changingCampus, setChangingCampus] = useState(false);
  // 381 (#1271): banning asks why, in a dialog.
  const [banning, setBanning] = useState(false);

  const { data: user, isLoading } = useQuery<User>({
    queryKey: RESOURCE_KEYS.user.one(id as string),
    queryFn: () => api.get(`/users/${id}`),
    enabled: Boolean(id),
  });

  const { data: bookingsData } = useQuery<BookingsResponse>({
    queryKey: ['user-bookings', id],
    queryFn: () => api.get(`/bookings?user_id=${id}&limit=10`),
    enabled: Boolean(id),
  });

  const toggleStatus = useMutation({
    mutationFn: (newStatus: string) => api.patch(`/users/${id}/status`, { status: newStatus }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.user.one(id as string) }),
  });

  // 336 (#761): staff resend the verification link. The platform decides (account rule, already verified, the
  // 2-minute gap, a failed send); the page shows what it answered.
  const { user: viewer } = useAuth();
  const resendVerification = useMutation({
    mutationFn: () => api.post(`/users/${id}/resend-verification`),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['user-events', id as string] }),
  });

  /**
   * 204 (#283, scoped): irreversible — permanently deletes the account's identity documents and
   * locks it out immediately. `window.confirm` rather than a Modal component: this is a single
   * detail-page action, not a list row, and no existing single-action confirm component fits here
   * (research.md R6).
   */
  const closeAccount = useMutation({
    mutationFn: () => api.post(`/users/${id}/close`, {}),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.user.one(id as string) }),
  });

  if (isLoading || !user) {
    return <div className="text-muted text-sm">Loading…</div>;
  }

  // 332 (FR-007): the platform's own answer; the page never works out "SuperAdmin" from a role name.
  const manageable = user.manageable !== false;
  // 333 (FR-005): the reason is the platform's too; only the wording is chosen here.
  const notCovered = user.manage_refusal === 'not_covered';
  const canDisable = user.status === 'active';
  const canEnable = user.status === 'banned' || user.status === 'inactive';
  // 332: the edit form reads the roles actually held (`user.roles`); the Admin/Customer/none reduction that used to be
  // computed here, and that made a SuperAdmin read "No role assigned", is gone.

  return (
    <div className="max-w-3xl">
      <PageHeader title={`${user.first_name} ${user.last_name}`} fallback="/users" />

      <div className="grid gap-4">
        <Card>
          <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">Profile</h2>
          <dl className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <dt className="text-steel">Status</dt>
            <dd>
              <StatusBadge status={user.status} />
              {user.status === 'banned' && user.ban && <BanReasonBox ban={user.ban} />}
            </dd>

            {/* 332 (US4-1): the roles actually held, read-only, visible even when the account actions are not. */}
            <dt className="text-steel">Roles</dt>
            <dd data-testid="user-roles" className="text-ink">
              {user.roles?.length ? user.roles.join(', ') : 'No role'}
            </dd>
            <dt className="text-steel">Email</dt>
            <dd>{user.email}</dd>

            <dt className="text-steel">Email verified</dt>
            <dd>
              {user.email_verified_at
                ? new Date(user.email_verified_at).toLocaleDateString('en-MY')
                : 'No'}
              {/* 336: only while unverified, for staff who manage users, on an account they may manage, not their own. */}
              {!user.email_verified_at &&
                canManageUsers &&
                manageable &&
                viewer?._id !== user._id && (
                  <div className="mt-2 space-y-1">
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={resendVerification.isPending}
                      onClick={() => resendVerification.mutate()}
                    >
                      Resend verification email
                    </Button>
                    {resendVerification.isSuccess && (
                      <p className="text-sm text-steel">Verification email sent.</p>
                    )}
                    {resendVerification.error instanceof Error && (
                      <p className="text-sm text-red-600">{resendVerification.error.message}</p>
                    )}
                  </div>
                )}
            </dd>

            {user.phone && (
              <>
                <dt className="text-steel">Phone</dt>
                <dd>{user.phone}</dd>
              </>
            )}

            {/* 366 (#1223): masked, Reveal recorded, Change/Clear with a reason; staff accounts show no row. */}
            <IdentityNumberRow
              userId={user._id}
              summary={user.identity_number ?? null}
              required={user.identity_number_required === true}
              canManage={canManageUsers && manageable}
            />

            <dt className="text-steel">Identity verification</dt>
            <dd>{user.identity_verification?.status ?? 'pending'}</dd>

            <dt className="text-steel">Joined</dt>
            <dd>{new Date(user.created_at).toLocaleDateString('en-MY')}</dd>

            {/* 330 (FR-011): the campus the customer sees and books at. */}
            <dt className="text-steel">Campus</dt>
            <dd className="flex items-center gap-2">
              <span data-testid="user-campus">
                {user.campus
                  ? `${user.campus.name}${user.campus.is_active ? '' : ' (inactive)'}`
                  : 'Not chosen yet'}
              </span>
              {canManageUsers && (
                <button
                  type="button"
                  data-testid="user-campus-change"
                  onClick={() => setChangingCampus(true)}
                  className="text-xs text-primary hover:underline"
                >
                  Change campus
                </button>
              )}
            </dd>
          </dl>
          {banning && (
            <BanDialog
              userId={user._id}
              userName={`${user.first_name} ${user.last_name}`.trim()}
              unpaidPenalties={user.unpaid_penalties ?? []}
              onClose={() => setBanning(false)}
            />
          )}
          {changingCampus && (
            <ChangeCampusModal
              userId={user._id}
              currentCampus={user.campus ?? null}
              onClose={() => setChangingCampus(false)}
            />
          )}

          {!manageable && notCovered && (
            <p data-testid="user-not-covered" className="mt-4 text-sm text-muted">
              You can't manage an account with more access than yours.
            </p>
          )}
          {!manageable && !notCovered && (
            <p data-testid="user-superadmin-only" className="mt-4 text-sm text-muted">
              Only a SuperAdmin can manage this account.
            </p>
          )}
          <div className="mt-4 flex gap-2">
            {manageable && canDisable && (
              <Button
                size="sm"
                variant="secondary"
                loading={toggleStatus.isPending}
                onClick={() => setBanning(true)}
              >
                Ban account
              </Button>
            )}
            {manageable && canEnable && (
              <Button
                size="sm"
                loading={toggleStatus.isPending}
                onClick={() => toggleStatus.mutate('active')}
              >
                Enable account
              </Button>
            )}
            <ImpersonateButton
              user={{
                _id: user._id,
                first_name: user.first_name,
                last_name: user.last_name,
                isAdministrator: user.is_administrator,
              }}
            />
            {manageable && !user.deleted_at && (
              <Button
                size="sm"
                variant="secondary"
                loading={closeAccount.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      'Close this account? Every identity document it has on file will be permanently deleted, and it will no longer be able to sign in. This cannot be undone.',
                    )
                  ) {
                    closeAccount.mutate();
                  }
                }}
              >
                Close account
              </Button>
            )}
          </div>
          {toggleStatus.isError && (
            <p className="mt-2 text-sm text-red-500">
              {(toggleStatus.error as { message?: string }).message ?? 'Failed to update status'}
            </p>
          )}
          {closeAccount.isError && (
            <p className="mt-2 text-sm text-red-500">
              {(closeAccount.error as { message?: string }).message ?? 'Failed to close account'}
            </p>
          )}
        </Card>

        {manageable && (
          <UserEditForm
            user={{
              _id: user._id,
              first_name: user.first_name,
              last_name: user.last_name,
              email: user.email,
              phone: user.phone,
              roles: user.roles,
              membership_roles: [...new Set((user.memberships ?? []).map((m) => m.role))],
              assignable_roles: user.assignable_roles,
              role_options: user.role_options,
            }}
          />
        )}

        <UserEventTimeline userId={id ?? ''} />

        <IdentityReview userId={id ?? ''} />

        <SupplierMemberships userId={id ?? ''} />

        <Card>
          <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
            Booking History
          </h2>
          <DataTable
            columns={bookingColumns}
            data={bookingsData?.items ?? []}
            loading={false}
            keyExtractor={(r) => r.id}
          />
        </Card>
      </div>
    </div>
  );
}
